import React, { useContext, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import Spinner from '../components/Spinner';
import { API_URL } from '../config';
import { getValidImageUrl } from '../utils/getImageUrl';

const Cart = () => {
  const { cartItems, removeFromCart, isCartLoading } = useContext(CartContext);
  const { user, token } = useContext(AuthContext);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const paymentFailed = new URLSearchParams(location.search).get('payment') === 'failed';

  const calculateTotal = () => {
    return cartItems.reduce((total, item) => total + parseFloat(item.newPrice || 0), 0).toFixed(2);
  };

  const originalTotal = () => {
    return cartItems.reduce((total, item) => total + parseFloat(item.oldPrice || item.newPrice || 0), 0).toFixed(2);
  };

  return (
    <div className="min-h-screen pt-24 px-margin-mobile md:px-margin-desktop max-w-[1200px] mx-auto pb-24">
      <h1 className="text-display-sm font-bold gold-gradient-text mb-8">Shopping Cart</h1>

      {isCartLoading ? (
        <Spinner text="Loading cart..." />
      ) : cartItems.length === 0 ? (
        <div className="glass-panel rounded-xl p-12 flex flex-col items-center justify-center text-center">
          <div className="w-24 h-24 bg-surface-container rounded-full flex items-center justify-center mb-6">
            <span className="material-symbols-outlined text-[48px] text-on-surface-variant">production_quantity_limits</span>
          </div>
          <h2 className="text-headline-sm font-bold mb-4">Your cart is empty</h2>
          <p className="text-on-surface-variant mb-8 max-w-md">Looks like you haven't added any Expert Advisors to your cart yet.</p>
          <Link to="/marketplace" className="bg-primary text-on-primary font-bold px-8 py-3 rounded-full hover:shadow-[0_0_15px_rgba(240,185,11,0.5)] transition-all">
            Browse EAs
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items List */}
          <div className="lg:col-span-2 space-y-4">
            {paymentFailed && (
              <div className="mb-6 bg-red-500/10 border border-red-500/30 text-red-400 p-4 rounded-lg flex items-center gap-3 animate-fade-in">
                <span className="material-symbols-outlined">error</span>
                <span>Checkout was cancelled or the payment failed. Please try again.</span>
              </div>
            )}
            {cartItems.map((item) => (
              <div key={item.id} className="glass-panel p-6 rounded-xl flex flex-col sm:flex-row items-start sm:items-center gap-6 relative">
                <img 
                  src={item.image ? getValidImageUrl(item.image) : "https://lh3.googleusercontent.com/aida/AP1WRLu3VM-9RrL8h0xwIcA8NzexoXX3SCvg6Ng64WiIwKsoA4SUZh3JOguhErzieVdekccqQhe_hTN3ZEaVsGBYwRwjPzAbGmrIdZklGxRMMxLoQto9Alpg9A77l-jWpAX6XFn7WGVw_CddPtgFLwZLRrH7-44IlMvufeyoIJoV-agMbSuW6uo420eY4V9C9gWgOD-vb15sbJHyPm6SgEDPygR1OBv1YizEtn9UNIusHPgsoS_Whm30nap0dCw2"} 
                  alt={item.name} 
                  className="w-24 h-24 rounded-lg object-cover bg-surface-container shrink-0 border border-white/5"
                />
                <div className="flex-grow">
                  <h3 className="text-title-lg font-bold mb-1">{item.name}</h3>
                  <p className="text-on-surface-variant text-sm mb-3">Lifetime License • Instant Delivery</p>
                  <div className="flex items-center gap-4">
                    <span className="font-bold text-primary text-title-lg">${item.newPrice}</span>
                    {item.oldPrice && <span className="line-through text-silver-text text-sm">${item.oldPrice}</span>}
                  </div>
                </div>
                <button 
                  onClick={() => removeFromCart(item.id)}
                  className="absolute top-4 right-4 text-on-surface-variant hover:text-danger-red transition-colors p-2"
                  aria-label="Remove item"
                >
                  <span className="material-symbols-outlined text-[20px]">delete</span>
                </button>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="glass-panel p-6 rounded-xl h-fit sticky top-24">
            <h3 className="text-headline-sm font-bold mb-6">Order Summary</h3>
            
            <div className="space-y-4 mb-6 border-b border-white/10 pb-6">
              <div className="flex justify-between text-silver-text">
                <span>Original Price</span>
                <span className="line-through">${originalTotal()}</span>
              </div>
              <div className="flex justify-between text-secondary">
                <span>Discounts</span>
                <span>-${(parseFloat(originalTotal()) - parseFloat(calculateTotal())).toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-bold text-title-lg pt-4 border-t border-white/5">
                <span>Total</span>
                <span className="text-primary">${calculateTotal()}</span>
              </div>
            </div>

            {!user ? (
              <div className="mb-6 bg-primary/10 border border-primary/20 rounded-lg p-4">
                <p className="text-sm mb-3">You must log in or create an account to proceed to checkout.</p>
                <Link to="/login" className="block w-full bg-surface-variant text-center py-3 rounded font-bold hover:bg-white/5 transition-colors mb-2">
                  Log In
                </Link>
                <Link to="/signup" className="block w-full border border-primary text-primary text-center py-3 rounded font-bold hover:bg-primary/5 transition-colors">
                  Create Account
                </Link>
              </div>
            ) : (
              <Link 
                to="/checkout"
                className="w-full font-bold py-4 rounded-lg flex items-center justify-center gap-2 transition-all bg-primary text-on-primary hover:shadow-[0_0_15px_rgba(240,185,11,0.5)]"
              >
                <span className="material-symbols-outlined">lock</span> Proceed to Checkout
              </Link>
            )}
            
            <p className="text-center text-xs text-on-surface-variant mt-4 flex justify-center items-center gap-1">
              <span className="material-symbols-outlined text-[14px]">verified_user</span> Safe & Secure Payment
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;
